import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NorthIndianChartRenderer, { NorthIndianPlanetLabel } from './NorthIndianChartRenderer';
import { getActualHouseShort, getHouseShort, getPlanetAbbr, getSignAbbr } from '../../i18n/astrology';
import { isOuterPlanetKey, KundliChartStyle, useUserPreferences } from '../../contexts/UserPreferencesContext';

interface NorthIndianChartProps {
  chartData: any;
  transitChartData?: any;
  size?: number;
  rotationHouse?: number;
  showFortune?: boolean;
  showHouseLabels?: boolean;
  showUpagrahas?: boolean;
  naturalZodiac?: boolean;
  showTransitOverlay?: boolean;
  chartStyleOverride?: KundliChartStyle;
}

const EXALT_SIGN: Record<string, number> = { SUN: 1, MOON: 2, MARS: 10, MERCURY: 6, JUPITER: 4, VENUS: 12, SATURN: 7, RAHU: 3, KETU: 9 };
const DEBIT_SIGN: Record<string, number> = { SUN: 7, MOON: 8, MARS: 4, MERCURY: 12, JUPITER: 10, VENUS: 6, SATURN: 1, RAHU: 9, KETU: 3 };
const COMBUST_ORB: Record<string, number> = { MOON: 12, MARS: 17, MERCURY: 14, JUPITER: 11, VENUS: 10, SATURN: 15 };
const UPAGRAHA_LABELS: Record<string, string> = {
  Mandi: 'Maṇḍi',
  Gulika: 'Gu',
  Dhuma: 'Dh',
  Vyatipata: 'Vy',
  Parivesha: 'Pa',
  Indrachapa: 'In',
  Upaketu: 'Uk',
};

const getPlanetStatus = (key: string, planet: any, sunLongitude: number) => {
  const signNumber = planet?.sign?.number ?? 0;
  const speed = planet?.speed ?? 0;
  let status = '';

  if (EXALT_SIGN[key] === signNumber) status += 'E';
  if (DEBIT_SIGN[key] === signNumber) status += 'D';
  if (speed < 0 && key !== 'RAHU' && key !== 'KETU') status += 'R';

  const combustOrb = COMBUST_ORB[key];
  if (combustOrb && key !== 'SUN') {
    let arc = Math.abs((planet?.longitude ?? 0) - sunLongitude);
    if (arc > 180) arc = 360 - arc;
    if (arc < combustOrb) status += 'C';
  }

  return status;
};

const normalizeStatuses = (computedStatus: string, planetStatus: any) => {
  const statuses = new Set<string>();

  if (Array.isArray(planetStatus)) {
    planetStatus.forEach((status) => {
      const normalized = String(status || '').trim();
      if (normalized) statuses.add(normalized === 'Db' ? 'D' : normalized);
    });
  }

  computedStatus.split('').forEach((status) => {
    if (status) statuses.add(status);
  });

  return Array.from(statuses);
};

const getPlanetTextColor = (statuses: string[]) => {
  if (statuses.includes('E')) return '#1b7f2a';
  if (statuses.includes('D') || statuses.includes('Db')) return '#d32f2f';
  if (statuses.includes('V')) return '#7b1fa2';
  if (statuses.includes('R')) return '#e65100';
  if (statuses.includes('C')) return '#455a64';
  if (statuses.includes('A')) return '#00897b';
  return '#1b5e20';
};

const getPartOfFortune = (chartData: any) => {
  const fortune = chartData?.kpAnalysis?.partOfFortune || chartData?.partOfFortune;
  const longitude = Number(fortune?.longitude);
  const signNumber = Number(fortune?.signNumber) || (Number.isFinite(longitude) ? Math.floor(longitude / 30) + 1 : null);

  if (!fortune || !Number.isFinite(longitude) || !signNumber) return null;

  return {
    longitude,
    signNumber,
    degree: Math.floor(((longitude % 30) + 30) % 30),
  };
};

const getUpagrahaEntry = (name: string, upagraha: any): NorthIndianPlanetLabel => {
  const degree = upagraha?.degree?.degrees ?? Math.floor((upagraha?.longitude ?? 0) % 30);
  const degreeText = Number.isFinite(Number(upagraha?.degreeInSign))
    ? Number(upagraha.degreeInSign).toFixed(2)
    : String(degree);

  return {
    abbr: upagraha?.shortName || UPAGRAHA_LABELS[name] || name.slice(0, 2),
    degree,
    color: '#7c3aed',
    isUpagraha: true,
    title: `${name} - ${degreeText}°${upagraha?.meaning ? `\n${upagraha.meaning}` : ''}`,
  };
};

const getLalKitabHouse = (planet: any, ascSign: number) => {
  const signNumber = Number(planet?.sign?.number);
  if (Number.isFinite(signNumber) && signNumber >= 1 && signNumber <= 12) {
    return ((signNumber - ascSign + 12) % 12) + 1;
  }

  return Number(planet?.d1House ?? planet?.rashiHouse ?? planet?.houseNumber ?? planet?.house ?? planet?.bhavaChalitHouse);
};

const NorthIndianChart: React.FC<NorthIndianChartProps> = ({
  chartData,
  transitChartData,
  size = 400,
  rotationHouse = 1,
  showFortune = false,
  showHouseLabels = false,
  showUpagrahas = false,
  naturalZodiac = false,
  showTransitOverlay = false,
  chartStyleOverride,
}) => {
  const { t } = useTranslation();
  const { chartStyle, showOuterPlanets } = useUserPreferences();
  const effectiveChartStyle = chartStyleOverride || chartStyle;
  const shouldShowPlanet = (key: string, planet: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key);
  const ascSign = chartData?.houses?.ascendant?.sign?.number ?? 1;

  const getActualHouseForDisplay = (displayHouse: number) => ((rotationHouse + displayHouse - 2) % 12) + 1;
  const getSignForHouse = (displayHouse: number) => {
    const actualHouse = getActualHouseForDisplay(displayHouse);
    if (naturalZodiac) return actualHouse;
    return ((ascSign + actualHouse - 2) % 12) + 1;
  };

  const getPlanetEntry = (key: string, planet: any, sunLongitude: number): NorthIndianPlanetLabel => {
    const name = String(planet?.name || key || '').toUpperCase();
    const degree = planet?.degree?.degrees ?? Math.floor((planet?.longitude ?? 0) % 30);
    const statuses = normalizeStatuses(getPlanetStatus(name, planet, sunLongitude), planet?.status);

    return {
      abbr: getPlanetAbbr(t, name),
      degree,
      status: statuses.join(''),
      color: getPlanetTextColor(statuses),
    };
  };

  const sunLongitude = chartData?.planets?.SUN?.longitude ?? chartData?.planets?.Sun?.longitude ?? 0;
  const fortune = showFortune ? getPartOfFortune(chartData) : null;
  const getPlanetsForSign = (sign: number): NorthIndianPlanetLabel[] => {
    const planets = Object.entries(chartData?.planets || {})
      .filter(([key, planet]: any) => shouldShowPlanet(key, planet))
      .filter(([, planet]: any) => planet?.sign?.number === sign)
      .map(([key, planet]: any) => getPlanetEntry(key, planet, sunLongitude));

    if (showTransitOverlay) {
      Object.entries(transitChartData?.planets || {})
        .filter(([key, planet]: any) => shouldShowPlanet(key, planet))
        .filter(([, planet]: any) => planet?.sign?.number === sign)
        .forEach(([key, planet]: any) => {
          const transitEntry = getPlanetEntry(key, planet, transitChartData?.planets?.SUN?.longitude ?? 0);
          planets.push({
            ...transitEntry,
            abbr: `T-${transitEntry.abbr}`,
            color: '#0369a1',
            isTransit: true,
            title: `${t('astrology.currentTransitPlanets')}: ${transitEntry.abbr}`,
          });
        });
    }

    if (showUpagrahas) {
      Object.entries(chartData?.upagrahas || {})
        .filter(([, upagraha]: any) => upagraha?.sign?.number === sign)
        .forEach(([name, upagraha]: any) => {
          planets.push(getUpagrahaEntry(name, upagraha));
        });
    }

    if (fortune?.signNumber === sign) {
      planets.push({
        abbr: t('astrology.partOfFortune'),
        degree: fortune.degree,
        status: '★',
        color: '#b45309',
        highlighted: true,
      });
    }

    return planets;
  };
  const houses = Array.from({ length: 12 }, (_, index) => {
    const id = index + 1;
    const targetSign = getSignForHouse(id);
    const actualHouse = getActualHouseForDisplay(id);
    const planets = Object.entries(chartData?.planets || {})
      .filter(([key, planet]: any) => shouldShowPlanet(key, planet))
      .filter(([, planet]: any) => naturalZodiac ? getLalKitabHouse(planet, ascSign) === actualHouse : planet?.sign?.number === targetSign)
      .map(([key, planet]: any) => getPlanetEntry(key, planet, sunLongitude));

    if (showTransitOverlay) {
      Object.entries(transitChartData?.planets || {})
        .filter(([key, planet]: any) => shouldShowPlanet(key, planet))
        .filter(([, planet]: any) => planet?.sign?.number === targetSign)
        .forEach(([key, planet]: any) => {
          const transitEntry = getPlanetEntry(key, planet, transitChartData?.planets?.SUN?.longitude ?? 0);
          planets.push({
            ...transitEntry,
            abbr: `T-${transitEntry.abbr}`,
            color: '#0369a1',
            isTransit: true,
            title: `${t('astrology.currentTransitPlanets')}: ${transitEntry.abbr}`,
          });
        });
    }

    if (showUpagrahas) {
      Object.entries(chartData?.upagrahas || {})
        .filter(([, upagraha]: any) => upagraha?.sign?.number === targetSign)
        .forEach(([name, upagraha]: any) => {
          planets.push(getUpagrahaEntry(name, upagraha));
        });
    }

    if (fortune?.signNumber === targetSign) {
      planets.push({
        abbr: t('astrology.partOfFortune'),
        degree: fortune.degree,
        status: '★',
        color: '#b45309',
        highlighted: true,
      });
    }

    return {
      id,
      houseLabel: getHouseShort(t, id),
      actualHouseLabel: rotationHouse !== 1 ? getActualHouseShort(t, getActualHouseForDisplay(id)) : undefined,
      signNumber: targetSign,
      signLabel: getSignAbbr(t, targetSign),
      planets,
    };
  });
  const signFixedHouses = Array.from({ length: 12 }, (_, index) => {
    const sign = index + 1;
    return {
      id: sign,
      houseLabel: getHouseShort(t, getHouseFromSign(sign, ascSign)),
      signNumber: sign,
      signLabel: getSignAbbr(t, sign),
      planets: getPlanetsForSign(sign),
    };
  });

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle1">
        {naturalZodiac
          ? 'Lal Kitab Chart • Aries in House 1'
          : effectiveChartStyle === 'south' || effectiveChartStyle === 'east'
            ? t('astrology.lagnaChartRashi')
            : `${t('astrology.lagnaChartRashi')} • ${t('astrology.rotatedFromHouse', { number: rotationHouse })}`}
      </Typography>
      {effectiveChartStyle === 'south' ? (
        <SouthIndianChartRenderer houses={signFixedHouses} size={size} ascSign={ascSign} />
      ) : effectiveChartStyle === 'east' ? (
        <EastIndianChartRenderer houses={signFixedHouses} size={size} ascSign={ascSign} />
      ) : (
        <NorthIndianChartRenderer houses={houses} size={size} showHouseLabels={showHouseLabels} />
      )}
    </Box>
  );
};

const planetToken = (planet: NorthIndianPlanetLabel) => `${planet.abbr}${planet.status || ''}${planet.degree ?? ''}`;

const planetDisplayColor = (planet: NorthIndianPlanetLabel) => {
  if (planet.highlighted) return '#b45309';
  if (planet.isTransit) return '#0277bd';
  if (planet.isUpagraha) return '#7c3aed';
  return planet.color || '#1b5e20';
};

const orderedPlanetTokens = (planets: NorthIndianPlanetLabel[]) => [
  ...planets.filter((planet) => !planet.isTransit && !planet.isUpagraha),
  ...planets.filter((planet) => planet.isTransit),
  ...planets.filter((planet) => planet.isUpagraha),
];

const signNames: Record<number, string> = {
  1: 'Ar',
  2: 'Ta',
  3: 'Ge',
  4: 'Cn',
  5: 'Le',
  6: 'Vi',
  7: 'Li',
  8: 'Sc',
  9: 'Sg',
  10: 'Cp',
  11: 'Aq',
  12: 'Pi',
};

const getHouseFromSign = (sign: number, ascendantSign: number) => ((sign - ascendantSign + 12) % 12) + 1;
const SIGN_HOUSE_LABEL_COLOR = '#111827';

const readableHouseLabel = (label?: string) => (label || '').replace(/,/g, ', ');

export const SouthIndianChartRenderer: React.FC<{ houses: any[]; size: number; ascSign: number }> = ({ houses, size, ascSign }) => {
  const cell = size / 4;
  const bySign = houses.reduce<Record<number, any>>((acc, house) => {
    acc[Number(house.signNumber)] = house;
    return acc;
  }, {});
  const signPositions: Record<number, { row: number; col: number }> = {
    12: { row: 0, col: 0 },
    1: { row: 0, col: 1 },
    2: { row: 0, col: 2 },
    3: { row: 0, col: 3 },
    11: { row: 1, col: 0 },
    4: { row: 1, col: 3 },
    10: { row: 2, col: 0 },
    5: { row: 2, col: 3 },
    9: { row: 3, col: 0 },
    8: { row: 3, col: 1 },
    7: { row: 3, col: 2 },
    6: { row: 3, col: 3 },
  };

  return (
    <Box sx={{ width: size, height: size, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', mx: 'auto', border: '2px solid', borderColor: 'primary.main', bgcolor: 'background.paper' }}>
      {Array.from({ length: 16 }).map((_, index) => {
        const row = Math.floor(index / 4);
        const col = index % 4;
        const sign = Number(Object.entries(signPositions).find(([, position]) => position.row === row && position.col === col)?.[0]);
        const house = bySign[sign];
        const isCenter = row > 0 && row < 3 && col > 0 && col < 3;
        const planetTokens = orderedPlanetTokens(house?.planets || []);

        return (
          <Box key={index} sx={{ width: cell, height: cell, border: isCenter ? 0 : '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
            {house ? (
              <>
                {sign === ascSign ? (
                  <Typography sx={{ color: 'success.main', fontSize: 10, fontWeight: 600, textAlign: 'center', lineHeight: 1.1 }}>
                    Lagna
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '3px 7px', lineHeight: 1.08, mt: 0.35 }}>
                  {planetTokens.map((planet, planetIndex) => (
                    <Typography
                      key={`${planet.abbr}-${planet.status || ''}-${planet.degree ?? ''}-${planetIndex}`}
                      component="span"
                      sx={{
                        color: planetDisplayColor(planet),
                        fontSize: { xs: 13, sm: 15, md: 16 },
                        fontWeight: 600,
                        px: planet.highlighted ? 0.4 : 0,
                        borderRadius: 0.75,
                        bgcolor: planet.highlighted ? '#fef3c7' : 'transparent',
                      }}
                    >
                      {planetToken(planet)}
                    </Typography>
                  ))}
                </Box>
              </>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

export const EastIndianChartRenderer: React.FC<{ houses: any[]; size: number; ascSign: number }> = ({ houses, size, ascSign }) => {
  const c = size / 2;
  const oneThird = size / 3;
  const twoThird = (size * 2) / 3;
  const bySign = houses.reduce<Record<number, any>>((acc, house) => {
    acc[Number(house.signNumber)] = house;
    return acc;
  }, {});
  const signPositions: Record<number, { x: number; y: number; maxChars?: number }> = {
    1: { x: c, y: oneThird * 0.45, maxChars: 10 },
    2: { x: oneThird * 0.75, y: oneThird * 0.2, maxChars: 9 },
    3: { x: oneThird * 0.25, y: oneThird * 0.8, maxChars: 8 },
    4: { x: oneThird * 0.38, y: c, maxChars: 9 },
    5: { x: oneThird * 0.25, y: twoThird + oneThird * 0.2, maxChars: 8 },
    6: { x: oneThird * 0.75, y: twoThird + oneThird * 0.8, maxChars: 8 },
    7: { x: c, y: twoThird + oneThird * 0.58, maxChars: 10 },
    8: { x: twoThird + oneThird * 0.25, y: twoThird + oneThird * 0.8, maxChars: 8 },
    9: { x: twoThird + oneThird * 0.75, y: twoThird + oneThird * 0.2, maxChars: 8 },
    10: { x: twoThird + oneThird * 0.62, y: c, maxChars: 9 },
    11: { x: twoThird + oneThird * 0.75, y: oneThird * 0.8, maxChars: 8 },
    12: { x: twoThird + oneThird * 0.25, y: oneThird * 0.2, maxChars: 8 },
  };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', margin: '0 auto', background: 'white', border: '2px solid #2e7d52' }}>
      <rect x="1" y="1" width={size - 2} height={size - 2} fill="white" stroke="#2e7d52" strokeWidth="2" />
      <g stroke="#6aa078" strokeWidth="1.5">
        <line x1={oneThird} y1="0" x2={oneThird} y2={size} />
        <line x1={twoThird} y1="0" x2={twoThird} y2={size} />
        <line x1="0" y1={oneThird} x2={size} y2={oneThird} />
        <line x1="0" y1={twoThird} x2={size} y2={twoThird} />
        <line x1="0" y1="0" x2={oneThird} y2={oneThird} />
        <line x1={size} y1="0" x2={twoThird} y2={oneThird} />
        <line x1="0" y1={size} x2={oneThird} y2={twoThird} />
        <line x1={size} y1={size} x2={twoThird} y2={twoThird} />
      </g>
      <text x={c} y={c - 5} fill="#2e7d52" fontSize="12" fontWeight="800" textAnchor="middle">East Indian</text>
      {Object.entries(signPositions).map(([signKey, position]) => {
        const sign = Number(signKey);
        const house = bySign[sign];
        const isAsc = sign === ascSign;
        const planetTokens = orderedPlanetTokens(house?.planets || []).slice(0, 6);
        const totalLines = planetTokens.length;
        const houseLabel = readableHouseLabel(house?.houseLabel);
        const lineHeight = Math.max(10, size * 0.015);
        const boxWidth = Math.max(70, Math.min(92, size * 0.21));
        const houseLabelOffset = houseLabel ? 11 : 0;
        const boxHeight = Math.max(42, 34 + houseLabelOffset + totalLines * lineHeight + (isAsc ? 10 : 0));
        const boxX = Math.max(6, Math.min(size - boxWidth - 6, position.x - boxWidth / 2));
        const boxY = Math.max(6, Math.min(size - boxHeight - 6, position.y - boxHeight / 2));
        const labelX = boxX + boxWidth / 2;
        const labelY = boxY + 16;
        const houseY = labelY + 11;
        const lagnaY = isAsc ? boxY + 30 + houseLabelOffset : null;
        const textStartY = boxY + (isAsc ? 44 : 34) + houseLabelOffset;
        return (
          <g key={sign}>
            <rect
              x={boxX}
              y={boxY}
              width={boxWidth}
              height={boxHeight}
              rx="5"
              fill="white"
              stroke="#6aa078"
              strokeWidth="0.4"
              opacity="0.98"
            />
            <text x={labelX} y={labelY} fill={SIGN_HOUSE_LABEL_COLOR} fontSize="10" fontWeight="800" textAnchor="middle">
              {signNames[sign]}
            </text>
            {houseLabel ? (
              <text x={labelX} y={houseY} fill={SIGN_HOUSE_LABEL_COLOR} fontSize="9" fontWeight="800" textAnchor="middle">
                {houseLabel}
              </text>
            ) : null}
            {isAsc && lagnaY ? (
              <text x={labelX} y={lagnaY} fill="#16a34a" fontSize="9" fontWeight="800" textAnchor="middle">
                Lagna
              </text>
            ) : null}
            {planetTokens.map((planet, planetIndex) => (
              <text
                key={`${sign}-${planet.abbr}-${planet.status || ''}-${planet.degree ?? ''}-${planetIndex}`}
                x={labelX}
                y={textStartY + planetIndex * lineHeight}
                fill={planetDisplayColor(planet)}
                fontSize="10"
                fontWeight="900"
                textAnchor="middle"
              >
                {planetToken(planet)}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
};

export default NorthIndianChart;
